import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';
import './Items.css';
import CreateNewItemModal from '../PurchaseEntries/CreateNewItemModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Pagination } from '@mui/material';
import {
  FaPills,
  FaExclamationTriangle,
  FaBoxes,
  FaTachometerAlt,
  FaExchangeAlt,
  FaSearch,
  FaTimes,
  FaPlus,
  FaSyncAlt,
  FaDownload,
  FaCheckDouble,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaLayerGroup,
  FaTag,
  FaWarehouse,
  FaCheckCircle,
  FaToggleOn,
  FaEdit,
  FaTrash,
  FaSlidersH,
  FaClock,
  FaClipboardList,
  FaPercentage,
  FaEye,
  FaEyeSlash,
  FaTimesCircle
} from 'react-icons/fa';

// ─── Default Initial Pharmacy Items (Rich Enterprise Seed Data) ───────────────
const INITIAL_SEED_ITEMS = [
  {
    id: 1,
    name: 'Paracetamol 650mg (Dolo 650)',
    brand: 'Dolo 650',
    composition: 'Paracetamol IP 650mg',
    manufacturer: 'Micro Labs Ltd',
    category: 'Tablet',
    type: 'Allopathy',
    packType: 'Strip (15 tabs)',
    rack: 'Rack A-1',
    availableStock: 240,
    mrp: 33.50,
    rate: 24.20,
    gstPercent: 12,
    batchNumber: 'DL65-9821',
    expiryDate: '2027-08-31',
    status: 'ACTIVE',
    thresholdQuantity: 30,
    fastSlowStatus: 'Fast',
    isMerged: false,
    hasAlternates: true,
    alternates: ['Calpol 650', 'Pacimol 650', 'Pyrigesic 650']
  },
  {
    id: 2,
    name: 'Amoxicillin & Potassium Clavulanate (Augmentin 625 Duo)',
    brand: 'Augmentin 625 Duo',
    composition: 'Amoxicillin 500mg + Clavulanic Acid 125mg',
    manufacturer: 'GlaxoSmithKline (GSK)',
    category: 'Tablet',
    type: 'Schedule H1',
    packType: 'Strip (10 tabs)',
    rack: 'Rack B-2',
    availableStock: 85,
    mrp: 201.50,
    rate: 154.00,
    gstPercent: 12,
    batchNumber: 'AUG-4412',
    expiryDate: '2027-04-30',
    status: 'ACTIVE',
    thresholdQuantity: 20,
    fastSlowStatus: 'Fast',
    isMerged: false,
    hasAlternates: true,
    alternates: ['Clavam 625', 'Moxikind-CV 625']
  },
  {
    id: 3,
    name: 'Pantoprazole Gastro-Resistant (Pan 40)',
    brand: 'Pan 40',
    composition: 'Pantoprazole Sodium IP 40mg',
    manufacturer: 'Alkem Laboratories Ltd',
    category: 'Tablet',
    type: 'Allopathy',
    packType: 'Strip (15 tabs)',
    rack: 'Rack A-3',
    availableStock: 190,
    mrp: 155.00,
    rate: 112.50,
    gstPercent: 12,
    batchNumber: 'PAN-7731',
    expiryDate: '2026-11-30', // Near expiry
    status: 'ACTIVE',
    thresholdQuantity: 25,
    fastSlowStatus: 'Fast',
    isMerged: false,
    hasAlternates: true,
    alternates: ['Pantocid 40', 'Pantodac 40']
  },
  {
    id: 4,
    name: 'Ceftriaxone Injection IP (Monocef 1g)',
    brand: 'Monocef 1g',
    composition: 'Ceftriaxone Sodium IP 1000mg',
    manufacturer: 'Aristo Pharmaceuticals',
    category: 'Injection',
    type: 'Schedule H1',
    packType: 'Vial with WFI',
    rack: 'Cold Storage C-1',
    availableStock: 45,
    mrp: 68.00,
    rate: 48.00,
    gstPercent: 12,
    batchNumber: 'MNF-1029',
    expiryDate: '2026-10-31', // Near expiry
    status: 'ACTIVE',
    thresholdQuantity: 15,
    fastSlowStatus: 'Fast',
    isMerged: false,
    hasAlternates: true,
    alternates: ['Taxim 1g', 'C-Tri 1g']
  },
  {
    id: 5,
    name: 'Azithromycin Tablets IP (Azee 500)',
    brand: 'Azee 500',
    composition: 'Azithromycin IP 500mg',
    manufacturer: 'Cipla Ltd',
    category: 'Tablet',
    type: 'Schedule H',
    packType: 'Strip (5 tabs)',
    rack: 'Rack B-1',
    availableStock: 14, // Low stock
    mrp: 119.50,
    rate: 86.00,
    gstPercent: 12,
    batchNumber: 'AZ-9980',
    expiryDate: '2027-12-31',
    status: 'ACTIVE',
    thresholdQuantity: 20,
    fastSlowStatus: 'Fast',
    isMerged: true,
    hasAlternates: true,
    alternates: ['Azithral 500', 'Zady 500']
  },
  {
    id: 6,
    name: 'Human Insulin 40IU/ml (Human Mixtard 30/70)',
    brand: 'Human Mixtard 30/70',
    composition: 'Biphasic Isophane Insulin Injection IP',
    manufacturer: 'Novo Nordisk',
    category: 'Injection',
    type: 'Schedule H',
    packType: 'Vial (10ml)',
    rack: 'Cold Storage C-2',
    availableStock: 22,
    mrp: 182.00,
    rate: 145.00,
    gstPercent: 5,
    batchNumber: 'NOV-2024',
    expiryDate: '2027-02-28',
    status: 'ACTIVE',
    thresholdQuantity: 10,
    fastSlowStatus: 'Fast',
    isMerged: false,
    hasAlternates: false,
    alternates: []
  },
  {
    id: 7,
    name: 'Cough Syrup (Ascoril D Plus 100ml)',
    brand: 'Ascoril D Plus',
    composition: 'Dextromethorphan + Chlorpheniramine + Phenylephrine',
    manufacturer: 'Glenmark Pharmaceuticals',
    category: 'Syrup',
    type: 'OTC',
    packType: 'Bottle (100ml)',
    rack: 'Rack D-1',
    availableStock: 0, // Out of stock
    mrp: 128.00,
    rate: 94.00,
    gstPercent: 12,
    batchNumber: 'ASC-8832',
    expiryDate: '2027-06-30',
    status: 'INACTIVE',
    thresholdQuantity: 15,
    fastSlowStatus: 'Slow',
    isMerged: false,
    hasAlternates: true,
    alternates: ['Benadryl DR', 'Zedex']
  },
  {
    id: 8,
    name: 'Metformin Hydrochloride (Glycomet 500 SR)',
    brand: 'Glycomet 500 SR',
    composition: 'Metformin Sustained Release IP 500mg',
    manufacturer: 'USV Private Ltd',
    category: 'Tablet',
    type: 'Allopathy',
    packType: 'Strip (20 tabs)',
    rack: 'Rack A-2',
    availableStock: 310,
    mrp: 48.00,
    rate: 34.50,
    gstPercent: 12,
    batchNumber: 'GLY-5120',
    expiryDate: '2028-01-31',
    status: 'ACTIVE',
    thresholdQuantity: 40,
    fastSlowStatus: 'Fast',
    isMerged: false,
    hasAlternates: true,
    alternates: ['Obimet 500', 'Cetapin 500']
  },
  {
    id: 9,
    name: 'IV Paracetamol Infusion (Paracip IV 100ml)',
    brand: 'Paracip IV',
    composition: 'Paracetamol IP 1000mg / 100ml',
    manufacturer: 'Cipla Ltd',
    category: 'IV Fluid',
    type: 'Allopathy',
    packType: 'Bottle (100ml)',
    rack: 'Rack E-1',
    availableStock: 6, // Low stock
    mrp: 320.00,
    rate: 215.00,
    gstPercent: 12,
    batchNumber: 'PC-4041',
    expiryDate: '2026-09-30', // Near expiry
    status: 'ACTIVE',
    thresholdQuantity: 15,
    fastSlowStatus: 'Slow',
    isMerged: false,
    hasAlternates: false,
    alternates: []
  },
  {
    id: 10,
    name: 'Normal Saline 0.9% (NS 500ml)',
    brand: 'NS 500ml',
    composition: 'Sodium Chloride 0.9% w/v',
    manufacturer: 'Otsuka Pharmaceutical',
    category: 'IV Fluid',
    type: 'Allopathy',
    packType: 'Bottle (500ml)',
    rack: 'Rack E-2',
    availableStock: 180,
    mrp: 45.00,
    rate: 26.00,
    gstPercent: 12,
    batchNumber: 'NS-7719',
    expiryDate: '2028-06-30',
    status: 'ACTIVE',
    thresholdQuantity: 50,
    fastSlowStatus: 'Fast',
    isMerged: false,
    hasAlternates: false,
    alternates: []
  },
  {
    id: 11,
    name: 'Diclofenac Sodium Gel (Voveran Emulgel 30g)',
    brand: 'Voveran Emulgel',
    composition: 'Diclofenac Diethylamine 1.16% w/w',
    manufacturer: 'Novartis India Ltd',
    category: 'Ointment',
    type: 'OTC',
    packType: 'Tube (30g)',
    rack: 'Rack D-3',
    availableStock: 48,
    mrp: 145.00,
    rate: 105.00,
    gstPercent: 12,
    batchNumber: 'VOV-3321',
    expiryDate: '2027-09-30',
    status: 'ACTIVE',
    thresholdQuantity: 10,
    fastSlowStatus: 'Slow',
    isMerged: false,
    hasAlternates: true,
    alternates: ['Volini Gel', 'Omnigel']
  },
  {
    id: 12,
    name: 'Sterile Surgical Gloves (Size 7.0)',
    brand: 'SurgiGlove 7.0',
    composition: 'Pre-powdered Latex Surgical Gloves',
    manufacturer: 'Hindustan Syringes (HMD)',
    category: 'Consumable',
    type: 'Surgical',
    packType: 'Box (50 pairs)',
    rack: 'Rack S-1',
    availableStock: 35,
    mrp: 450.00,
    rate: 310.00,
    gstPercent: 12,
    batchNumber: 'GLV-9011',
    expiryDate: '2028-03-31',
    status: 'ACTIVE',
    thresholdQuantity: 10,
    fastSlowStatus: 'Fast',
    isMerged: false,
    hasAlternates: false,
    alternates: []
  }
];

/**
 * Items Component - Pharmacy Items Management Screen
 * Developed according to our Enterprise Design System (.unified-master-card, .pe-table, .th-filter-trigger)
 */
const Items = () => {
  // ─── Primary State ────────────────────────────────────────────────────────
  const [items, setItems] = useState(INITIAL_SEED_ITEMS);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ─── Sub-Tab Navigation ───────────────────────────────────────────────────
  // 'ALL' | 'NEAR_EXPIRY' | 'MERGED' | 'SLOW_FAST' | 'ALTERNATES'
  const [activeSubTab, setActiveSubTab] = useState('ALL');

  // ─── Modal States ─────────────────────────────────────────────────────────
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [selectedItemForBatches, setSelectedItemForBatches] = useState(null);
  const [stockToAdjust, setStockToAdjust] = useState(null);
  const [adjustQuantityInput, setAdjustQuantityInput] = useState('');

  // ─── Card Value Visibility Toggle ("Show" feature from screenshot) ────────
  const [showStockValue, setShowStockValue] = useState(true);

  // ─── Search & Categories ──────────────────────────────────────────────────
  const [searchCategory, setSearchCategory] = useState('all'); // 'all' | 'name' | 'category' | 'rack' | 'composition'
  const [searchQuery, setSearchQuery] = useState('');

  // ─── Column-Specific Popover Filters ──────────────────────────────────────
  const [filters, setFilters] = useState({
    item: '',
    category: 'ALL',
    type: 'ALL',
    rack: '',
    stockStatus: 'ALL', // 'ALL' | 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK'
    status: 'ALL'       // 'ALL' | 'ACTIVE' | 'INACTIVE'
  });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeRowActionsId, setActiveRowActionsId] = useState(null);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);

  const filterBarRef = useRef(null);

  // ─── Pagination ───────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // ─── Load Initial Data from Backend ───────────────────────────────────────
  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(ENDPOINTS.GET_ALL_MEDICINES || `${ENDPOINTS.API_BASE_URL}/getAllMedicines`);
      if (res.data && Array.isArray(res.data) && res.data.length > 0) {
        // Map backend medicine model to UI standard
        const mapped = res.data.map((m, idx) => ({
          id: m.medicineId || m.id || idx + 1,
          name: m.medicineBrand || m.brandName || m.name || `Medicine #${idx + 1}`,
          brand: m.medicineBrand || m.brandName || '',
          composition: m.composition || '',
          manufacturer: m.manufacturer || '',
          category: m.category || 'Tablet',
          type: m.itemType || m.type || 'Allopathy',
          packType: m.packType || m.unit || 'Strip',
          rack: m.rack || 'Rack A-1',
          availableStock: Number(m.stockQuantity ?? m.quantity ?? 50),
          mrp: Number(m.medicinePrice ?? m.packMrp ?? 100),
          rate: Number(m.packRate ?? 70),
          gstPercent: Number(m.gstPercent ?? 12),
          batchNumber: m.batchNumber || 'BAT-001',
          expiryDate: m.expiryDate || '2027-12-31',
          status: m.isAvailable === false ? 'INACTIVE' : (m.status || 'ACTIVE'),
          thresholdQuantity: Number(m.thresholdQuantity || 15),
          fastSlowStatus: (m.fastThresholdValue ? 'Fast' : 'Slow'),
          isMerged: false,
          hasAlternates: false,
          alternates: []
        }));
        setItems(mapped);
      }
    } catch (err) {
      console.warn('Backend medicines API offline or unavailable, using initial pharmaceutical catalog:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchItems();
    toast.info('Refreshing items catalog...', { autoClose: 1500 });
  };

  // ─── Click Outside Listener ───────────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterBarRef.current && !filterBarRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
      if (!e.target.closest('.supplier-row-actions-dropdown-wrapper')) {
        setActiveRowActionsId(null);
      }
      if (!e.target.closest('.download-dropdown-container')) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Sub-Tab & Filter Logic ───────────────────────────────────────────────
  const isNearExpiry = (dateStr) => {
    if (!dateStr) return false;
    const exp = new Date(dateStr);
    const now = new Date();
    const diffMonths = (exp.getFullYear() - now.getFullYear()) * 12 + (exp.getMonth() - now.getMonth());
    return diffMonths <= 3; // within 3 months or already expired
  };

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      // 1. Sub-Tab filter
      if (activeSubTab === 'NEAR_EXPIRY' && !isNearExpiry(item.expiryDate)) {
        return false;
      }
      if (activeSubTab === 'MERGED' && !item.isMerged) {
        return false;
      }
      if (activeSubTab === 'SLOW_FAST' && !item.fastSlowStatus) {
        return false;
      }
      if (activeSubTab === 'ALTERNATES' && (!item.hasAlternates || item.alternates.length === 0)) {
        return false;
      }

      // 2. Fused Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        if (searchCategory === 'name') {
          if (!item.name.toLowerCase().includes(q)) return false;
        } else if (searchCategory === 'category') {
          if (!item.category.toLowerCase().includes(q)) return false;
        } else if (searchCategory === 'rack') {
          if (!item.rack.toLowerCase().includes(q)) return false;
        } else if (searchCategory === 'composition') {
          if (!(item.composition || '').toLowerCase().includes(q)) return false;
        } else {
          // 'all'
          const hay = `${item.name} ${item.composition} ${item.category} ${item.rack} ${item.manufacturer}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
      }

      // 3. Column Header Filters
      if (filters.item && !item.name.toLowerCase().includes(filters.item.toLowerCase())) {
        return false;
      }
      if (filters.category !== 'ALL' && item.category !== filters.category) {
        return false;
      }
      if (filters.type !== 'ALL' && item.type !== filters.type) {
        return false;
      }
      if (filters.rack && !item.rack.toLowerCase().includes(filters.rack.toLowerCase())) {
        return false;
      }
      if (filters.stockStatus === 'IN_STOCK' && item.availableStock <= (item.thresholdQuantity || 15)) {
        return false;
      }
      if (filters.stockStatus === 'LOW_STOCK' && (item.availableStock <= 0 || item.availableStock > (item.thresholdQuantity || 15))) {
        return false;
      }
      if (filters.stockStatus === 'OUT_OF_STOCK' && item.availableStock > 0) {
        return false;
      }
      if (filters.status !== 'ALL' && item.status !== filters.status) {
        return false;
      }

      return true;
    });
  }, [items, activeSubTab, searchQuery, searchCategory, filters]);

  // ─── Metric Calculations ──────────────────────────────────────────────────
  const totalItemsCount = items.length;
  const nearExpiryCount = useMemo(() => items.filter((i) => isNearExpiry(i.expiryDate)).length, [items]);
  const lowStockCount = useMemo(() => items.filter((i) => i.availableStock > 0 && i.availableStock <= (i.thresholdQuantity || 15)).length, [items]);
  const outOfStockCount = useMemo(() => items.filter((i) => i.availableStock === 0).length, [items]);
  const totalStockValue = useMemo(() => {
    return items.reduce((acc, curr) => acc + (curr.availableStock * (curr.rate || curr.mrp || 0)), 0);
  }, [items]);

  // ─── Pagination Calculations ──────────────────────────────────────────────
  const totalPages = Math.ceil(filteredItems.length / rowsPerPage) || 1;
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, currentPage, rowsPerPage]);

  const hasActiveFilters =
    searchQuery ||
    filters.item ||
    filters.category !== 'ALL' ||
    filters.type !== 'ALL' ||
    filters.rack ||
    filters.stockStatus !== 'ALL' ||
    filters.status !== 'ALL';

  const clearAllFilters = () => {
    setSearchQuery('');
    setFilters({
      item: '',
      category: 'ALL',
      type: 'ALL',
      rack: '',
      stockStatus: 'ALL',
      status: 'ALL'
    });
    setActiveDropdown(null);
    setCurrentPage(1);
  };

  // ─── Status Toggle ────────────────────────────────────────────────────────
  const handleToggleStatus = (id) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, status: item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' }
          : item
      )
    );
    toast.success('Item status updated successfully', { autoClose: 1500 });
  };

  // ─── Stock Adjustment ─────────────────────────────────────────────────────
  const handleSaveStockAdjustment = () => {
    if (!stockToAdjust) return;
    const qty = parseInt(adjustQuantityInput, 10);
    if (isNaN(qty) || qty < 0) {
      toast.error('Please enter a valid stock quantity.');
      return;
    }
    setItems((prev) =>
      prev.map((item) =>
        item.id === stockToAdjust.id
          ? { ...item, availableStock: qty, status: qty === 0 ? 'INACTIVE' : item.status }
          : item
      )
    );
    toast.success(`Stock updated for ${stockToAdjust.brand || stockToAdjust.name}`, { autoClose: 2000 });
    setStockToAdjust(null);
    setAdjustQuantityInput('');
  };

  // ─── Delete Item ──────────────────────────────────────────────────────────
  const handleDeleteItem = (id) => {
    if (window.confirm('Are you sure you want to delete this pharmacy item?')) {
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success('Pharmacy item removed successfully', { autoClose: 2000 });
    }
  };

  // ─── CSV Download Export ──────────────────────────────────────────────────
  const handleDownloadCSV = (withGst = false) => {
    if (filteredItems.length === 0) {
      toast.warning('No items available to export.');
      return;
    }
    const headers = withGst
      ? ['Item Name', 'Category', 'Type', 'Rack', 'Available Stock', 'MRP', 'Rate', 'GST %', 'Batch No', 'Expiry Date', 'Status']
      : ['Item Name', 'Category', 'Type', 'Rack', 'Available Stock', 'MRP', 'Status'];

    const rows = filteredItems.map((i) =>
      withGst
        ? [
            `"${i.name.replace(/"/g, '""')}"`,
            `"${i.category}"`,
            `"${i.type}"`,
            `"${i.rack}"`,
            i.availableStock,
            i.mrp.toFixed(2),
            i.rate.toFixed(2),
            `${i.gstPercent}%`,
            `"${i.batchNumber}"`,
            i.expiryDate,
            i.status
          ]
        : [
            `"${i.name.replace(/"/g, '""')}"`,
            `"${i.category}"`,
            `"${i.type}"`,
            `"${i.rack}"`,
            i.availableStock,
            i.mrp.toFixed(2),
            i.status
          ]
    );

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', withGst ? 'pharmacy_items_gst_export.csv' : 'pharmacy_items_catalog.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsDownloadOpen(false);
    toast.success('Download started successfully!');
  };

  // ─── Categories & Types for Filter Popovers ───────────────────────────────
  const uniqueCategories = useMemo(() => {
    const set = new Set(items.map((i) => i.category).filter(Boolean));
    return Array.from(set);
  }, [items]);

  const uniqueTypes = useMemo(() => {
    const set = new Set(items.map((i) => i.type).filter(Boolean));
    return Array.from(set);
  }, [items]);

  return (
    <div className="vt-items-page-container">
      <ToastContainer position="top-right" autoClose={2500} />

      {/* ─── 1. Top Sub-Tabs Bar & "+ Add Items" CTA ───────────────────────── */}
      <div className="vt-items-top-bar">
        <div className="vt-items-subtabs-list">
          <button
            type="button"
            className={`vt-items-subtab-btn ${activeSubTab === 'ALL' ? 'active' : ''}`}
            onClick={() => {
              setActiveSubTab('ALL');
              setCurrentPage(1);
            }}
          >
            <FaPills className="subtab-icon" />
            <span>Pharmacy Items</span>
          </button>

          <button
            type="button"
            className={`vt-items-subtab-btn ${activeSubTab === 'NEAR_EXPIRY' ? 'active' : ''}`}
            onClick={() => {
              setActiveSubTab('NEAR_EXPIRY');
              setCurrentPage(1);
            }}
          >
            <FaExclamationTriangle className="subtab-icon icon-warn" />
            <span>Near Expiry / Expired Items</span>
            {nearExpiryCount > 0 && <span className="subtab-badge warn-badge">{nearExpiryCount}</span>}
          </button>

          <button
            type="button"
            className={`vt-items-subtab-btn ${activeSubTab === 'MERGED' ? 'active' : ''}`}
            onClick={() => {
              setActiveSubTab('MERGED');
              setCurrentPage(1);
            }}
          >
            <FaBoxes className="subtab-icon" />
            <span>Merged Items</span>
          </button>

          <button
            type="button"
            className={`vt-items-subtab-btn ${activeSubTab === 'SLOW_FAST' ? 'active' : ''}`}
            onClick={() => {
              setActiveSubTab('SLOW_FAST');
              setCurrentPage(1);
            }}
          >
            <FaTachometerAlt className="subtab-icon" />
            <span>Slow / Fast Items</span>
          </button>

          <button
            type="button"
            className={`vt-items-subtab-btn ${activeSubTab === 'ALTERNATES' ? 'active' : ''}`}
            onClick={() => {
              setActiveSubTab('ALTERNATES');
              setCurrentPage(1);
            }}
          >
            <FaExchangeAlt className="subtab-icon" />
            <span>Alternate Items</span>
          </button>
        </div>

        {/* "+ Add Items" Primary Action Button */}
        <button
          type="button"
          className="master-new-appt-btn vt-items-add-btn"
          onClick={() => setIsCreateModalOpen(true)}
          title="Add a new pharmacy item"
        >
          <FaPlus size={12} /> Add Items
        </button>
      </div>

      {/* ─── 2. Carousel Stat Cards & Control Actions Row ───────────────────── */}
      <div className="vt-items-cards-row">
        {/* Card 1: Total Items */}
        <div className="vt-items-stat-card">
          <div className="vt-items-card-accent blue-accent" />
          <div className="vt-items-card-icon blue-theme">
            <FaClipboardList size={20} />
          </div>
          <div className="vt-items-card-content">
            <span className="vt-items-card-val">{totalItemsCount.toLocaleString()}</span>
            <span className="vt-items-card-lbl">Total Items</span>
          </div>
        </div>

        {/* Card 2: Current Stock Value (GST wise) */}
        <div className="vt-items-stat-card">
          <div className="vt-items-card-accent purple-accent" />
          <div className="vt-items-card-icon purple-theme">
            <FaPercentage size={20} />
          </div>
          <div className="vt-items-card-content">
            <div className="stock-value-wrapper">
              <span className="vt-items-card-val">
                {showStockValue
                  ? `₹${totalStockValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : '••••••••••'}
              </span>
              <button
                type="button"
                className="stock-toggle-show-btn"
                onClick={() => setShowStockValue((prev) => !prev)}
                title={showStockValue ? 'Hide stock value' : 'Show stock value'}
              >
                {showStockValue ? <FaEyeSlash size={12} /> : <FaEye size={12} />}
                <span>{showStockValue ? 'Hide' : 'Show'}</span>
              </button>
            </div>
            <span className="vt-items-card-lbl">% Current Stock Value (GST wise)</span>
          </div>
        </div>

        {/* Card 3: Low Stock & Near Expiry Alert */}
        <div className="vt-items-stat-card">
          <div className="vt-items-card-accent amber-accent" />
          <div className="vt-items-card-icon amber-theme">
            <FaExclamationTriangle size={18} />
          </div>
          <div className="vt-items-card-content">
            <span className="vt-items-card-val warn-text">{lowStockCount + nearExpiryCount}</span>
            <span className="vt-items-card-lbl">Action Required (Low/Exp)</span>
          </div>
        </div>

        {/* Right Side Utility Actions */}
        <div className="vt-items-stat-actions">
          {/* Verification CTA Button (Orange badge button matching screenshot) */}
          <button
            type="button"
            className="vt-items-verification-btn"
            onClick={() => setIsVerificationModalOpen(true)}
            title="Physical stock verification"
          >
            <FaCheckDouble size={13} />
            <span>Verification</span>
          </button>

          {/* Download Dropdown */}
          <div className="download-dropdown-container">
            <button
              type="button"
              className={`master-toolbar-icon-btn ${isDownloadOpen ? 'active' : ''}`}
              onClick={() => setIsDownloadOpen((prev) => !prev)}
              title="Download items report"
            >
              <FaDownload size={13} />
              <FaChevronDown className="download-chevron" size={10} />
            </button>

            {isDownloadOpen && (
              <div className="download-dropdown-menu">
                <span className="download-dropdown-header">Export Pharmacy Items</span>
                <button
                  type="button"
                  className="download-dropdown-item"
                  onClick={() => handleDownloadCSV(false)}
                >
                  <div className="download-item-icon standard">
                    <FaDownload size={14} />
                  </div>
                  <div className="download-item-info">
                    <span className="download-item-title">Standard Items List</span>
                    <span className="download-item-sub">Basic stock, MRP & categories</span>
                  </div>
                </button>
                <button
                  type="button"
                  className="download-dropdown-item"
                  onClick={() => handleDownloadCSV(true)}
                >
                  <div className="download-item-icon gst">
                    <FaPercentage size={14} />
                  </div>
                  <div className="download-item-info">
                    <span className="download-item-title">Items List (with GST & Batches)</span>
                    <span className="download-item-sub">Rates, GST %, batch & expiry</span>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* Refresh Button */}
          <button
            type="button"
            className="master-toolbar-icon-btn"
            onClick={handleRefresh}
            title="Refresh items list"
          >
            <FaSyncAlt className={isRefreshing ? 'vt-spinning' : ''} size={13} />
          </button>
        </div>
      </div>

      {/* ─── 3. Master Unified Enterprise Card ──────────────────────────────── */}
      <div className="unified-master-card">
        <div className="master-card-accent-bar" />

        {/* Master Toolbar Header: Fused Search */}
        <div className="master-toolbar-header">
          <div className="toolbar-left-group">
            <div className="fused-search-group">
              <select
                value={searchCategory}
                onChange={(e) => {
                  setSearchCategory(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Fields</option>
                <option value="name">Item / Brand Name</option>
                <option value="category">Category</option>
                <option value="rack">Rack / Location</option>
                <option value="composition">Composition</option>
              </select>

              <div className="search-input-wrapper">
                <input
                  type="text"
                  placeholder={
                    searchCategory === 'name'
                      ? 'Search item or brand name...'
                      : searchCategory === 'category'
                      ? 'Search by category (Tablet, Syrup, Injection)...'
                      : searchCategory === 'rack'
                      ? 'Search by rack (Rack A-1, Cold Storage)...'
                      : searchCategory === 'composition'
                      ? 'Search active composition...'
                      : 'Search Pharmacy Items...'
                  }
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="search-clear-btn"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="toolbar-right-group">
            {hasActiveFilters && (
              <button
                type="button"
                className="master-secondary-btn reset-btn"
                onClick={clearAllFilters}
                title="Reset active filters"
              >
                <FaTimes size={11} /> Reset Filters
              </button>
            )}
          </div>
        </div>

        {/* Active Filter Chips Banner */}
        {hasActiveFilters && (
          <div className="pe-active-filters-chips">
            <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 600 }}>Active Filters:</span>
            {searchQuery && (
              <span className="pe-filter-chip">
                Search: "{searchQuery}"
                <span className="pe-filter-chip-remove" onClick={() => setSearchQuery('')}>
                  <FaTimes />
                </span>
              </span>
            )}
            {filters.item && (
              <span className="pe-filter-chip">
                Item: {filters.item}
                <span className="pe-filter-chip-remove" onClick={() => setFilters((p) => ({ ...p, item: '' }))}>
                  <FaTimes />
                </span>
              </span>
            )}
            {filters.category !== 'ALL' && (
              <span className="pe-filter-chip">
                Category: {filters.category}
                <span className="pe-filter-chip-remove" onClick={() => setFilters((p) => ({ ...p, category: 'ALL' }))}>
                  <FaTimes />
                </span>
              </span>
            )}
            {filters.type !== 'ALL' && (
              <span className="pe-filter-chip">
                Type: {filters.type}
                <span className="pe-filter-chip-remove" onClick={() => setFilters((p) => ({ ...p, type: 'ALL' }))}>
                  <FaTimes />
                </span>
              </span>
            )}
            {filters.rack && (
              <span className="pe-filter-chip">
                Rack: {filters.rack}
                <span className="pe-filter-chip-remove" onClick={() => setFilters((p) => ({ ...p, rack: '' }))}>
                  <FaTimes />
                </span>
              </span>
            )}
            {filters.stockStatus !== 'ALL' && (
              <span className="pe-filter-chip">
                Stock: {filters.stockStatus.replace('_', ' ')}
                <span className="pe-filter-chip-remove" onClick={() => setFilters((p) => ({ ...p, stockStatus: 'ALL' }))}>
                  <FaTimes />
                </span>
              </span>
            )}
            {filters.status !== 'ALL' && (
              <span className="pe-filter-chip">
                Status: {filters.status}
                <span className="pe-filter-chip-remove" onClick={() => setFilters((p) => ({ ...p, status: 'ALL' }))}>
                  <FaTimes />
                </span>
              </span>
            )}
            <span className="suppliers-clear-all-link" onClick={clearAllFilters}>
              Clear All
            </span>
          </div>
        )}

        {/* ─── 4. Master Data Table (Millimetric Column Alignment) ─────────── */}
        <div className="master-table-container">
          <table className="master-data-table pe-table">
            <thead ref={filterBarRef} className={activeDropdown ? 'has-open-dropdown' : ''}>
              <tr>
                {/* 1. Item Column */}
                <th
                  className={activeDropdown === 'item' ? 'th-popover-open' : ''}
                  style={{ minWidth: '240px', width: '25%', position: 'relative', zIndex: activeDropdown === 'item' ? 999999 : 1 }}
                >
                  <div
                    className={`th-filter-trigger ${filters.item ? 'active' : ''}`}
                    onClick={() => setActiveDropdown((prev) => (prev === 'item' ? null : 'item'))}
                    title="Filter by Item Name"
                  >
                    <div className="th-filter-title">
                      <FaPills className="th-icon" />
                      <span>Item</span>
                      <FaChevronDown className="th-caret" />
                    </div>
                  </div>

                  {activeDropdown === 'item' && (
                    <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                      <label className="popover-title">Filter by Item Name</label>
                      <input
                        type="text"
                        className="header-filter-input"
                        placeholder="Search item or brand..."
                        value={filters.item}
                        onChange={(e) => {
                          setFilters((p) => ({ ...p, item: e.target.value }));
                          setCurrentPage(1);
                        }}
                        autoFocus
                      />
                      <div className="popover-btn-group">
                        <button
                          type="button"
                          className="pop-btn-clear"
                          onClick={() => setFilters((p) => ({ ...p, item: '' }))}
                        >
                          Clear
                        </button>
                        <button
                          type="button"
                          className="pop-btn-apply"
                          onClick={() => setActiveDropdown(null)}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </th>

                {/* 2. Category Column */}
                <th
                  className={activeDropdown === 'category' ? 'th-popover-open' : ''}
                  style={{ minWidth: '150px', width: '13%', position: 'relative', zIndex: activeDropdown === 'category' ? 999999 : 1 }}
                >
                  <div
                    className={`th-filter-trigger ${filters.category !== 'ALL' ? 'active' : ''}`}
                    onClick={() => setActiveDropdown((prev) => (prev === 'category' ? null : 'category'))}
                    title="Filter by Category"
                  >
                    <div className="th-filter-title">
                      <FaLayerGroup className="th-icon" />
                      <span>Category</span>
                      <FaChevronDown className="th-caret" />
                    </div>
                  </div>

                  {activeDropdown === 'category' && (
                    <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                      <label className="popover-title">Select Category</label>
                      <select
                        className="header-filter-select"
                        value={filters.category}
                        onChange={(e) => {
                          setFilters((p) => ({ ...p, category: e.target.value }));
                          setCurrentPage(1);
                        }}
                        autoFocus
                      >
                        <option value="ALL">All Categories</option>
                        {uniqueCategories.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      <div className="popover-btn-group">
                        <button
                          type="button"
                          className="pop-btn-clear"
                          onClick={() => {
                            setFilters((p) => ({ ...p, category: 'ALL' }));
                            setActiveDropdown(null);
                          }}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className="pop-btn-apply"
                          onClick={() => setActiveDropdown(null)}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </th>

                {/* 3. Type Column */}
                <th
                  className={activeDropdown === 'type' ? 'th-popover-open' : ''}
                  style={{ minWidth: '140px', width: '12%', position: 'relative', zIndex: activeDropdown === 'type' ? 999999 : 1 }}
                >
                  <div
                    className={`th-filter-trigger ${filters.type !== 'ALL' ? 'active' : ''}`}
                    onClick={() => setActiveDropdown((prev) => (prev === 'type' ? null : 'type'))}
                    title="Filter by Type"
                  >
                    <div className="th-filter-title">
                      <FaTag className="th-icon" />
                      <span>Type</span>
                      <FaChevronDown className="th-caret" />
                    </div>
                  </div>

                  {activeDropdown === 'type' && (
                    <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                      <label className="popover-title">Select Item Type</label>
                      <select
                        className="header-filter-select"
                        value={filters.type}
                        onChange={(e) => {
                          setFilters((p) => ({ ...p, type: e.target.value }));
                          setCurrentPage(1);
                        }}
                        autoFocus
                      >
                        <option value="ALL">All Types</option>
                        {uniqueTypes.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <div className="popover-btn-group">
                        <button
                          type="button"
                          className="pop-btn-clear"
                          onClick={() => {
                            setFilters((p) => ({ ...p, type: 'ALL' }));
                            setActiveDropdown(null);
                          }}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className="pop-btn-apply"
                          onClick={() => setActiveDropdown(null)}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </th>

                {/* 4. Rack Column */}
                <th
                  className={activeDropdown === 'rack' ? 'th-popover-open' : ''}
                  style={{ minWidth: '150px', width: '13%', position: 'relative', zIndex: activeDropdown === 'rack' ? 999999 : 1 }}
                >
                  <div
                    className={`th-filter-trigger ${filters.rack ? 'active' : ''}`}
                    onClick={() => setActiveDropdown((prev) => (prev === 'rack' ? null : 'rack'))}
                    title="Filter by Rack / Shelf"
                  >
                    <div className="th-filter-title">
                      <FaWarehouse className="th-icon" />
                      <span>Rack</span>
                      <FaChevronDown className="th-caret" />
                    </div>
                  </div>

                  {activeDropdown === 'rack' && (
                    <div className="table-header-popover" onClick={(e) => e.stopPropagation()}>
                      <label className="popover-title">Filter by Rack / Location</label>
                      <input
                        type="text"
                        className="header-filter-input"
                        placeholder="e.g. Rack A-1, Cold Store..."
                        value={filters.rack}
                        onChange={(e) => {
                          setFilters((p) => ({ ...p, rack: e.target.value }));
                          setCurrentPage(1);
                        }}
                        autoFocus
                      />
                      <div className="popover-btn-group">
                        <button
                          type="button"
                          className="pop-btn-clear"
                          onClick={() => setFilters((p) => ({ ...p, rack: '' }))}
                        >
                          Clear
                        </button>
                        <button
                          type="button"
                          className="pop-btn-apply"
                          onClick={() => setActiveDropdown(null)}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </th>

                {/* 5. Available Column */}
                <th
                  className={activeDropdown === 'available' ? 'th-popover-open' : ''}
                  style={{ minWidth: '140px', width: '13%', textAlign: 'center', position: 'relative', zIndex: activeDropdown === 'available' ? 999999 : 1 }}
                >
                  <div
                    className={`th-filter-trigger ${filters.stockStatus !== 'ALL' ? 'active' : ''}`}
                    onClick={() => setActiveDropdown((prev) => (prev === 'available' ? null : 'available'))}
                    title="Filter by Available Stock"
                  >
                    <div className="th-filter-title">
                      <FaCheckCircle className="th-icon" />
                      <span>Available</span>
                      <FaChevronDown className="th-caret" />
                    </div>
                  </div>

                  {activeDropdown === 'available' && (
                    <div className="table-header-popover" style={{ minWidth: '200px' }} onClick={(e) => e.stopPropagation()}>
                      <label className="popover-title">Filter by Stock Availability</label>
                      <select
                        className="header-filter-select"
                        value={filters.stockStatus}
                        onChange={(e) => {
                          setFilters((p) => ({ ...p, stockStatus: e.target.value }));
                          setCurrentPage(1);
                        }}
                        autoFocus
                      >
                        <option value="ALL">All Stock Levels</option>
                        <option value="IN_STOCK">In Stock (&gt; 15 units)</option>
                        <option value="LOW_STOCK">Low Stock (1 - 15 units)</option>
                        <option value="OUT_OF_STOCK">Out of Stock (0 units)</option>
                      </select>
                      <div className="popover-btn-group">
                        <button
                          type="button"
                          className="pop-btn-clear"
                          onClick={() => {
                            setFilters((p) => ({ ...p, stockStatus: 'ALL' }));
                            setActiveDropdown(null);
                          }}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className="pop-btn-apply"
                          onClick={() => setActiveDropdown(null)}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </th>

                {/* 6. Status Column */}
                <th
                  className={activeDropdown === 'status' ? 'th-popover-open' : ''}
                  style={{ minWidth: '110px', width: '10%', textAlign: 'center', position: 'relative', zIndex: activeDropdown === 'status' ? 999999 : 1 }}
                >
                  <div
                    className={`th-filter-trigger ${filters.status !== 'ALL' ? 'active' : ''}`}
                    onClick={() => setActiveDropdown((prev) => (prev === 'status' ? null : 'status'))}
                    title="Filter by Status"
                  >
                    <div className="th-filter-title">
                      <FaToggleOn className="th-icon" />
                      <span>Status</span>
                      <FaChevronDown className="th-caret" />
                    </div>
                  </div>

                  {activeDropdown === 'status' && (
                    <div className="table-header-popover" style={{ minWidth: '180px' }} onClick={(e) => e.stopPropagation()}>
                      <label className="popover-title">Filter by Status</label>
                      <select
                        className="header-filter-select"
                        value={filters.status}
                        onChange={(e) => {
                          setFilters((p) => ({ ...p, status: e.target.value }));
                          setCurrentPage(1);
                        }}
                        autoFocus
                      >
                        <option value="ALL">All Status</option>
                        <option value="ACTIVE">Active Only</option>
                        <option value="INACTIVE">Inactive Only</option>
                      </select>
                      <div className="popover-btn-group">
                        <button
                          type="button"
                          className="pop-btn-clear"
                          onClick={() => {
                            setFilters((p) => ({ ...p, status: 'ALL' }));
                            setActiveDropdown(null);
                          }}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className="pop-btn-apply"
                          onClick={() => setActiveDropdown(null)}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </th>

                {/* 7. Actions Column */}
                <th style={{ minWidth: '110px', width: '10%', textAlign: 'center' }}>
                  <div className="th-filter-trigger static">
                    <div className="th-filter-title">
                      <span>Actions</span>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>

            {/* Permanent tbody: handles loading, empty and data rows */}
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="table-state-cell">
                    <div className="table-loading-state">
                      <FaClock size={32} color="#64748b" />
                      <p>Loading Pharmacy Items Catalog...</p>
                    </div>
                  </td>
                </tr>
              ) : paginatedItems.length === 0 ? (
                <tr>
                  <td colSpan={7} className="table-state-cell">
                    <div className="table-empty-state">
                      <FaSearch size={32} color="#94a3b8" />
                      <h4>No Pharmacy Items Found</h4>
                      <p>No records match your selected criteria. Try adjusting the search query or adding a new item.</p>
                      <button
                        type="button"
                        className="master-new-appt-btn"
                        style={{ marginTop: 12 }}
                        onClick={() => setIsCreateModalOpen(true)}
                      >
                        <FaPlus size={12} /> Add New Item
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedItems.map((item, index) => {
                  const isNearBottom = paginatedItems.length > 4 && index >= paginatedItems.length - 2;
                  const isRowActionOpen = activeRowActionsId === item.id;

                  // Stock availability status calculation
                  const isOutOfStock = item.availableStock === 0;
                  const isLowStock = item.availableStock > 0 && item.availableStock <= (item.thresholdQuantity || 15);

                  return (
                    <tr
                      key={item.id}
                      className={`table-data-row ${isRowActionOpen ? 'row-actions-active' : ''}`}
                      style={isRowActionOpen ? { position: 'relative', zIndex: 100 } : undefined}
                    >
                      {/* 1. Item Name & Details */}
                      <td>
                        <div className="item-name-cell">
                          <span className="item-main-name">{item.name}</span>
                          <div className="item-sub-tags">
                            {item.composition && (
                              <span className="item-composition-sub" title={item.composition}>
                                {item.composition}
                              </span>
                            )}
                            {item.manufacturer && (
                              <span className="item-mfg-sub">
                                • {item.manufacturer}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* 2. Category */}
                      <td>
                        <span className="item-category-badge">{item.category}</span>
                      </td>

                      {/* 3. Type */}
                      <td>
                        <span className="item-type-badge">{item.type}</span>
                      </td>

                      {/* 4. Rack / Shelf */}
                      <td>
                        <span className="item-rack-badge" title={item.rack}>
                          <FaWarehouse size={11} style={{ marginRight: 5, color: '#0284c7' }} />
                          {item.rack || '—'}
                        </span>
                      </td>

                      {/* 5. Available Stock */}
                      <td style={{ textAlign: 'center' }}>
                        <span
                          className={`pe-mono-chip ${
                            isOutOfStock
                              ? 'stock-out'
                              : isLowStock
                              ? 'stock-low'
                              : 'stock-available'
                          }`}
                          title={
                            isOutOfStock
                              ? 'Out of Stock'
                              : isLowStock
                              ? `Low Stock (${item.availableStock} units remaining)`
                              : `${item.availableStock} units available`
                          }
                        >
                          {item.availableStock} {item.packType?.includes('tab') ? 'tabs' : 'units'}
                        </span>
                      </td>

                      {/* 6. Status Toggle */}
                      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <label
                          className="supplier-status-toggle"
                          title={`Click to ${item.status === 'ACTIVE' ? 'deactivate' : 'activate'} this item`}
                        >
                          <input
                            type="checkbox"
                            checked={item.status === 'ACTIVE'}
                            onChange={() => handleToggleStatus(item.id)}
                          />
                          <span className="supplier-toggle-slider" />
                        </label>
                      </td>

                      {/* 7. Row Actions Dropdown */}
                      <td
                        style={{
                          textAlign: 'center',
                          overflow: 'visible',
                          position: 'relative',
                          zIndex: isRowActionOpen ? 100 : 'auto'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="supplier-row-actions-dropdown-wrapper">
                          <button
                            type="button"
                            className={`supplier-row-actions-trigger ${isRowActionOpen ? 'active' : ''}`}
                            onClick={() =>
                              setActiveRowActionsId(activeRowActionsId === item.id ? null : item.id)
                            }
                            title="Actions"
                          >
                            Actions <FaChevronDown className="actions-caret" />
                          </button>

                          {isRowActionOpen && (
                            <div className={`supplier-row-actions-menu ${isNearBottom ? 'open-upwards' : ''}`}>
                              {/* View Batches / Details */}
                              <button
                                type="button"
                                className="supplier-row-action-item"
                                onClick={() => {
                                  setSelectedItemForBatches(item);
                                  setActiveRowActionsId(null);
                                }}
                              >
                                <FaBoxes className="action-menu-icon icon-blue" />
                                <span>Batch & Stock Details</span>
                              </button>

                              {/* Adjust Stock */}
                              <button
                                type="button"
                                className="supplier-row-action-item"
                                onClick={() => {
                                  setStockToAdjust(item);
                                  setAdjustQuantityInput(String(item.availableStock));
                                  setActiveRowActionsId(null);
                                }}
                              >
                                <FaSlidersH className="action-menu-icon icon-green" />
                                <span>Adjust Stock</span>
                              </button>

                              {/* Edit Item */}
                              <button
                                type="button"
                                className="supplier-row-action-item"
                                onClick={() => {
                                  toast.info(`Editing ${item.brand || item.name}`);
                                  setActiveRowActionsId(null);
                                }}
                              >
                                <FaEdit className="action-menu-icon" />
                                <span>Edit Item Details</span>
                              </button>

                              {/* Delete */}
                              <div className="supplier-menu-divider" />
                              <button
                                type="button"
                                className="supplier-row-action-item delete-item"
                                onClick={() => {
                                  handleDeleteItem(item.id);
                                  setActiveRowActionsId(null);
                                }}
                              >
                                <FaTrash className="action-menu-icon" />
                                <span>Delete Item</span>
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ─── 5. Master Pagination Footer ─────────────────────────────────── */}
        <div className="master-pagination-footer">
          <div className="pagination-count">
            Showing page <strong>{currentPage}</strong> of <strong>{totalPages || 1}</strong> ({filteredItems.length} items)
          </div>

          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, val) => setCurrentPage(val)}
            color="primary"
            size="small"
            shape="rounded"
          />
        </div>
      </div>

      {/* ─── 6. Create New Item Modal ───────────────────────────────────────── */}
      <CreateNewItemModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={(newItem) => {
          if (newItem) {
            setItems((prev) => [
              {
                id: newItem.id || Date.now(),
                name: newItem.brand || newItem.itemName || 'New Item',
                brand: newItem.brand || newItem.itemName || '',
                composition: newItem.composition || '',
                manufacturer: newItem.manufacturer || '',
                category: newItem.category || 'Tablet',
                type: newItem.itemType || 'Allopathy',
                packType: newItem.packType || 'Strip',
                rack: newItem.rack || 'Rack A-1',
                availableStock: Number(newItem.quantity || 0),
                mrp: Number(newItem.mrp || newItem.packMrp || 0),
                rate: Number(newItem.rate || newItem.packRate || 0),
                gstPercent: Number(newItem.gstPercent || 12),
                batchNumber: newItem.batchNumber || 'BAT-NEW',
                expiryDate: newItem.expiryDate || '2028-12-31',
                status: 'ACTIVE',
                thresholdQuantity: Number(newItem.thresholdQuantity || 15),
                fastSlowStatus: 'Fast',
                isMerged: false,
                hasAlternates: false,
                alternates: []
              },
              ...prev
            ]);
            toast.success('New pharmacy item added successfully!');
          }
          setIsCreateModalOpen(false);
        }}
      />

      {/* ─── 7. Stock Verification Modal ────────────────────────────────────── */}
      {isVerificationModalOpen && (
        <div className="vt-item-modal-overlay" onClick={() => setIsVerificationModalOpen(false)}>
          <div className="vt-item-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="vt-item-modal-header">
              <div className="vt-item-modal-title-group">
                <div className="vt-item-modal-badge amber-badge">
                  <FaCheckDouble />
                </div>
                <div>
                  <h3 className="vt-item-modal-title">Physical Stock Verification</h3>
                  <p className="vt-item-modal-sub">Compare system quantity against physical rack counts</p>
                </div>
              </div>
              <button
                type="button"
                className="vt-item-modal-close"
                onClick={() => setIsVerificationModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="vt-item-modal-body">
              <div className="verification-summary-cards">
                <div className="verif-card">
                  <span className="verif-val">{totalItemsCount}</span>
                  <span className="verif-lbl">Total Catalog Items</span>
                </div>
                <div className="verif-card success">
                  <span className="verif-val">{items.filter((i) => i.availableStock > (i.thresholdQuantity || 15)).length}</span>
                  <span className="verif-lbl">Sufficient Stock</span>
                </div>
                <div className="verif-card warning">
                  <span className="verif-val">{lowStockCount}</span>
                  <span className="verif-lbl">Low Stock Alerts</span>
                </div>
                <div className="verif-card danger">
                  <span className="verif-val">{outOfStockCount}</span>
                  <span className="verif-lbl">Out of Stock (Zero)</span>
                </div>
              </div>

              <p style={{ fontSize: '13px', color: '#475569', marginTop: 14 }}>
                <strong>Verification Routine:</strong> Please inspect designated cold-storage and rack bins. Use the "Adjust Stock" action on individual rows to reconcile any physical discrepancy.
              </p>
            </div>

            <div className="vt-item-modal-footer">
              <button
                type="button"
                className="master-secondary-btn"
                onClick={() => setIsVerificationModalOpen(false)}
              >
                Close
              </button>
              <button
                type="button"
                className="master-new-appt-btn"
                onClick={() => {
                  toast.success('Stock verification audit log initiated.');
                  setIsVerificationModalOpen(false);
                }}
              >
                <FaCheckDouble size={12} /> Confirm Audit Log
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── 8. Batch & Stock Details Modal ─────────────────────────────────── */}
      {selectedItemForBatches && (
        <div className="vt-item-modal-overlay" onClick={() => setSelectedItemForBatches(null)}>
          <div className="vt-item-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="vt-item-modal-header">
              <div className="vt-item-modal-title-group">
                <div className="vt-item-modal-badge blue-badge">
                  <FaBoxes />
                </div>
                <div>
                  <h3 className="vt-item-modal-title">{selectedItemForBatches.name}</h3>
                  <p className="vt-item-modal-sub">
                    Rack: {selectedItemForBatches.rack} • Category: {selectedItemForBatches.category}
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="vt-item-modal-close"
                onClick={() => setSelectedItemForBatches(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="vt-item-modal-body">
              <div className="batch-info-grid">
                <div className="batch-info-field">
                  <span className="batch-lbl">Batch Number</span>
                  <span className="batch-val font-mono">{selectedItemForBatches.batchNumber}</span>
                </div>
                <div className="batch-info-field">
                  <span className="batch-lbl">Expiry Date</span>
                  <span className="batch-val">{selectedItemForBatches.expiryDate}</span>
                </div>
                <div className="batch-info-field">
                  <span className="batch-lbl">Pack MRP</span>
                  <span className="batch-val">₹{selectedItemForBatches.mrp?.toFixed(2)}</span>
                </div>
                <div className="batch-info-field">
                  <span className="batch-lbl">Purchase Rate</span>
                  <span className="batch-val">₹{selectedItemForBatches.rate?.toFixed(2)}</span>
                </div>
                <div className="batch-info-field">
                  <span className="batch-lbl">GST Applicable</span>
                  <span className="batch-val">{selectedItemForBatches.gstPercent}%</span>
                </div>
                <div className="batch-info-field">
                  <span className="batch-lbl">Available Quantity</span>
                  <span className="batch-val highlight-val">{selectedItemForBatches.availableStock} units</span>
                </div>
              </div>

              {selectedItemForBatches.hasAlternates && selectedItemForBatches.alternates?.length > 0 && (
                <div className="item-alternates-box">
                  <span className="alt-title">Available Substitutes / Alternates:</span>
                  <div className="alt-chips-list">
                    {selectedItemForBatches.alternates.map((alt, idx) => (
                      <span key={idx} className="alt-pill">
                        <FaPills size={10} style={{ marginRight: 4 }} /> {alt}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="vt-item-modal-footer">
              <button
                type="button"
                className="master-secondary-btn"
                onClick={() => setSelectedItemForBatches(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── 9. Stock Adjustment Modal ──────────────────────────────────────── */}
      {stockToAdjust && (
        <div className="vt-item-modal-overlay" onClick={() => setStockToAdjust(null)}>
          <div className="vt-item-modal-container adjust-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vt-item-modal-header">
              <div className="vt-item-modal-title-group">
                <div className="vt-item-modal-badge green-badge">
                  <FaSlidersH />
                </div>
                <div>
                  <h3 className="vt-item-modal-title">Adjust Stock Quantity</h3>
                  <p className="vt-item-modal-sub">{stockToAdjust.brand || stockToAdjust.name}</p>
                </div>
              </div>
              <button
                type="button"
                className="vt-item-modal-close"
                onClick={() => setStockToAdjust(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="vt-item-modal-body">
              <div className="adjust-form-group">
                <label className="adjust-form-label">Physical Count / New Available Stock (Units):</label>
                <input
                  type="number"
                  className="adjust-form-input"
                  min="0"
                  value={adjustQuantityInput}
                  onChange={(e) => setAdjustQuantityInput(e.target.value)}
                  autoFocus
                />
                <span className="adjust-hint">
                  Current system stock is <strong>{stockToAdjust.availableStock}</strong> units.
                </span>
              </div>
            </div>

            <div className="vt-item-modal-footer">
              <button
                type="button"
                className="master-secondary-btn"
                onClick={() => setStockToAdjust(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="master-new-appt-btn"
                onClick={handleSaveStockAdjustment}
              >
                Save Stock
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Items;
